// src/components/Header.jsx
// Responsive header — desktop: full row, mobile: logo + tier badge + hamburger drawer

import { useState, useEffect, useRef } from "react";
import LoginButton from "./LoginButton";
import LanguageSwitcher from "./LanguageSwitcher";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";

export default function Header({ onUpgradeClick }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const drawerRef = useRef(null);

  const { user, accessType, promptsLeft, promptsLimit, isLifetime } = useAuth();
  const { t } = useLanguage();

  // Detect screen size
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // Close drawer bila klik luar
  useEffect(() => {
    const handler = (e) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) {
        setDrawerOpen(false);
      }
    };
    if (drawerOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [drawerOpen]);

  // Tier badge config
  const tierBadge = {
    free:     { label: "Free",              color: "#666" },
    starter:  { label: t("tier_starter"),   color: "#f59e0b" },
    popular:  { label: t("tier_popular"),   color: "#10b981" },
    lifetime: { label: "✦ " + t("tier_lifetime"), color: "#a855f7" },
  }[accessType];

  const TierBadge = ({ style: extraStyle }) => tierBadge ? (
    <div style={{
      background: `${tierBadge.color}22`, border: `1px solid ${tierBadge.color}55`,
      borderRadius: 10, padding: "5px 10px", fontSize: 11,
      color: tierBadge.color, fontWeight: 700, ...extraStyle
    }}>
      {tierBadge.label}
    </div>
  ) : null;

  const PromptCounter = () => user && !isLifetime ? (
    <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#0c0c0f", border: "1px solid #2a2a3a", borderRadius: 10, padding: "8px 14px" }}>
      <span style={{ fontSize: 13, color: promptsLeft <= 3 ? "#ef4444" : "#6366f1" }}>⚡</span>
      <span style={{ fontSize: 13, color: promptsLeft <= 3 ? "#ef4444" : "#888" }}>
        {t("counter_left").replace("{left}", promptsLeft).replace("{limit}", promptsLimit)}
      </span>
      <button onClick={() => { onUpgradeClick(); setDrawerOpen(false); }}
        style={{ fontSize: 12, color: "#a855f7", background: "none", border: "none", cursor: "pointer", fontWeight: 600, padding: 0 }}>
        {t("upgrade_arrow")}
      </button>
    </div>
  ) : null;

  return (
    <>
      <style>{`
        @keyframes drawerSlide {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>

      <header style={{
        borderBottom: "1px solid #1e1e2e", padding: "0 24px",
        position: "sticky", top: 0,
        background: "rgba(12,12,15,0.95)", backdropFilter: "blur(12px)", zIndex: 50
      }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", height: 64 }}>

          {/* Logo */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="/logo192.png" alt="Promptix" style={{ width: 40, height: 40, borderRadius: 8 }} />
            <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 18, letterSpacing: "-0.5px" }}>Promptix</span>
          </div>

          {/* ── DESKTOP ── */}
          {!isMobile && (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {user && !isLifetime && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#13131a", border: "1px solid #2a2a3a", borderRadius: 10, padding: "6px 12px" }}>
                  <span style={{ fontSize: 12, color: promptsLeft <= 3 ? "#ef4444" : "#6366f1" }}>⚡</span>
                  <span style={{ fontSize: 13, color: promptsLeft <= 3 ? "#ef4444" : "#888" }}>
                    {t("counter_left").replace("{left}", promptsLeft).replace("{limit}", promptsLimit)}
                  </span>
                  <button onClick={onUpgradeClick}
                    style={{ fontSize: 11, color: "#a855f7", background: "none", border: "none", cursor: "pointer", fontWeight: 600, padding: 0 }}>
                    {t("upgrade_arrow")}
                  </button>
                </div>
              )}
              <TierBadge />
              <LanguageSwitcher />
              <LoginButton />
            </div>
          )}

          {/* ── MOBILE ── */}
          {isMobile && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>

                {/* Profile photo */}
                {user?.photoURL && (
                <img
                    src={user.photoURL}
                    alt="profile"
                    style={{ width: 28, height: 28, borderRadius: "50%", border: "1px solid #2a2a3a", objectFit: "cover" }}
                />
                )}

                {/* Tier badge */}
                <TierBadge />

                {/* Hamburger */}
                <button
                onClick={() => setDrawerOpen(o => !o)}
                style={{
                    background: "#13131a", border: "1px solid #2a2a3a",
                    borderRadius: 10, cursor: "pointer",
                    display: "flex", flexDirection: "column", gap: 4,
                    alignItems: "center", justifyContent: "center",
                    width: 40, height: 40,
                }}
                >
                <span style={{ display: "block", width: 18, height: 2, background: "#888", borderRadius: 2 }} />
                <span style={{ display: "block", width: 18, height: 2, background: "#888", borderRadius: 2 }} />
                <span style={{ display: "block", width: 18, height: 2, background: "#888", borderRadius: 2 }} />
                </button>
            </div>
            )}
        </div>
      </header>

      {/* ── MOBILE DRAWER ── */}
      {isMobile && drawerOpen && (
        <>
          {/* Overlay */}
          <div
            onClick={() => setDrawerOpen(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 98 }}
          />

          {/* Drawer */}
          <div
            ref={drawerRef}
            style={{
              position: "fixed", top: 0, right: 0, bottom: 0,
              width: 280, background: "#13131a",
              borderLeft: "1px solid #2a2a3a", zIndex: 99,
              padding: "24px 20px", display: "flex", flexDirection: "column", gap: 16,
              animation: "drawerSlide 0.25s ease",
            }}
          >
            {/* Close button */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 16 }}>Menu</span>
              <button
                onClick={() => setDrawerOpen(false)}
                style={{ background: "none", border: "none", color: "#666", cursor: "pointer", fontSize: 22, lineHeight: 1 }}
              >×</button>
            </div>

            {/* Divider */}
            <div style={{ height: 1, background: "#1e1e2e" }} />

            {/* Prompt counter */}
            <PromptCounter />

            {/* Tier info kalau lifetime */}
            {user && isLifetime && (
              <div style={{ background: "rgba(168,85,247,0.1)", border: "1px solid rgba(168,85,247,0.3)", borderRadius: 12, padding: "12px 14px" }}>
                <div style={{ fontSize: 13, color: "#a855f7", fontWeight: 700 }}>✦ Lifetime Access</div>
                <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>Unlimited prompts, never expires</div>
              </div>
            )}

            {/* Language switcher */}
            <div>
              <div style={{ fontSize: 11, color: "#555", fontWeight: 600, letterSpacing: 1, marginBottom: 8 }}>LANGUAGE</div>
              <LanguageSwitcher />
            </div>

            {/* Divider */}
            <div style={{ height: 1, background: "#1e1e2e" }} />

            {/* Login/Logout */}
            <div onClick={() => setDrawerOpen(false)}>
              <LoginButton />
            </div>

          </div>
        </>
      )}
    </>
  );
}